import React, { useEffect, useState } from "react";
import { CalendarClock, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";

const DEFAULT_TOPICS = `Crédit | Courtier en crédit immobilier à Vitré : comment ça marche | courtier vitré
Assurance | Assurance emprunteur à Laval : les garanties à comparer | assurance emprunteur Laval
Assurance | Assurance emprunteur : comprendre les garanties | comparatif assurance emprunteur garanties
Assurance | Changer d'assurance emprunteur sans changer de banque | assurance emprunteur sans changer de banque
Assurance | Courtier en assurance emprunteur à Rennes | courtier assurance emprunteur ille et vilaine
Assurance | Courtier en assurance emprunteur à Laval | courtier en assurance emprunteur Laval
Crédit | Crédit immobilier : bien préparer son projet | courtier crédit immobilier
Crédit | Crédit immobilier dans l'ancien en Ille-et-Vilaine | credit immobilier ancien ille et vilaine
Crédit | Financer un logement neuf : les étapes clés | credit immobilier logement neuf
Crédit | Taux immobilier : comprendre les éléments de votre prêt | taux credit immobilier
Placement | Assurance-vie et patrimoine : les bons repères | assurance vie
Placement | SCPI : définition et fonctionnement | scpi c'est quoi
Professionnels | Assurance homme-clé : protéger son entreprise | assurance homme clé entreprise`;

const tomorrow = () => new Date(Date.now() + 86400000).toISOString().slice(0, 10);
const field = "w-full bg-md-bg border border-md-border rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-md-secondary";

export default function BatchPlanner({ onDone }) {
  const { authFetch } = useAuth();
  const [open, setOpen] = useState(false);
  const [topics, setTopics] = useState(DEFAULT_TOPICS);
  const [start, setStart] = useState(tomorrow());
  const [perWeek, setPerWeek] = useState(3);
  const [job, setJob] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!job || job.status !== "running") return undefined;
    const t = setInterval(async () => {
      const j = await (await authFetch(`/admin/articles/batch/${job.id}`)).json();
      setJob(j);
      if (j.status === "finished") onDone();
    }, 4000);
    return () => clearInterval(t);
  }, [job]); // eslint-disable-line react-hooks/exhaustive-deps

  const launch = async () => {
    setError("");
    const list = topics.split("\n").map((l) => l.split("|").map((x) => x.trim())).filter((p) => p.length >= 2 && p[1]);
    const payload = { topics: list.map(([category, theme, keyword = ""]) => ({ category, theme, keyword })), start_date: start, per_week: perWeek, hour: 9 };
    const r = await authFetch("/admin/articles/batch", { method: "POST", body: JSON.stringify(payload) });
    const d = await r.json();
    if (!r.ok) return setError(typeof d.detail === "string" ? d.detail : "Vérifiez le format : Catégorie | Sujet | mot-clé");
    setJob(d);
  };

  const pct = job ? Math.round(((job.done + job.failed) / job.total) * 100) : 0;

  return (
    <>
      <button data-testid="batch-open-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 border border-md-border bg-white hover:border-md-primary text-md-primary px-5 py-3 rounded-full font-semibold transition-colors">
        <CalendarClock className="w-4 h-4" /> Programmer une série
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="batch-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-md-primary font-medium">Programmer une série d'articles</DialogTitle>
            <DialogDescription>Chaque ligne : Catégorie | Sujet | mot-clé ciblé. Les articles sont rédigés par l'IA puis programmés à 9h — vous pouvez les relire, modifier ou supprimer avant leur publication.</DialogDescription>
          </DialogHeader>
          {job ? (
            <div className="space-y-4 py-4" data-testid="batch-progress">
              <div className="flex items-center gap-2 text-md-primary font-semibold">
                {job.status === "running" && <Loader2 className="w-4 h-4 animate-spin" />}
                {job.status === "running" ? "Rédaction en cours…" : "Terminé"} — {job.done}/{job.total} programmés{job.failed ? `, ${job.failed} en échec` : ""}
              </div>
              <div className="h-2 bg-md-bg rounded-full overflow-hidden"><div className="h-full bg-md-secondary transition-all" style={{ width: `${pct}%` }} /></div>
              {job.errors?.length > 0 && <p className="text-xs text-red-600">Échecs : {job.errors.join(" · ")}</p>}
              {job.status === "finished" && <button data-testid="batch-close-btn" onClick={() => { setOpen(false); setJob(null); }} className="bg-md-primary text-white px-5 py-2.5 rounded-full text-sm font-semibold">Fermer</button>}
            </div>
          ) : (
            <div className="space-y-4">
              <textarea data-testid="batch-topics" rows={12} value={topics} onChange={(e) => setTopics(e.target.value)} className={`${field} font-mono text-xs`} />
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block"><span className="text-[10px] uppercase tracking-widest text-md-muted font-semibold">Première publication à partir du</span>
                  <input data-testid="batch-start" type="date" value={start} min={tomorrow()} onChange={(e) => setStart(e.target.value)} className={field} /></label>
                <label className="block"><span className="text-[10px] uppercase tracking-widest text-md-muted font-semibold">Rythme</span>
                  <select data-testid="batch-rhythm" value={perWeek} onChange={(e) => setPerWeek(Number(e.target.value))} className={field}>
                    <option value={3}>3 par semaine (lun, mer, ven)</option><option value={2}>2 par semaine (mar, jeu)</option>
                  </select></label>
              </div>
              {error && <p data-testid="batch-error" className="text-sm text-red-600">{error}</p>}
              <div className="flex justify-end">
                <button data-testid="batch-launch-btn" onClick={launch} className="bg-md-secondary hover:bg-md-secondaryHover text-white px-6 py-3 rounded-full text-sm font-semibold">Rédiger et programmer</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
